/**
 * Module dependencies.
 */

import { FormList } from 'app/components/organisms/crud/form-types';
import { requiredRule } from 'app/core/utils/field-rules';
import { useGetDialCodes } from 'app/hooks/app-requests/use-get-dial-codes';
import { DialCode } from 'app/types/dial-code';
import { TFunction } from 'react-i18next';
import React from 'react';
import { DialCountry } from 'app/components/atoms/country/dial-country';
import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import { usePaginatedCrudRequest } from 'app/hooks/app-requests/crud/use-paginated-crud-request';
import { User } from 'app/types/user';

/**
 * Left.
 */

const left = (translate: TFunction): FormList => [
  {
    type: 'render',
    name: 'leftWire1',
    render: <SubTitle>{translate('pharmacyForm.pharmacyTitle')}</SubTitle>
  },
  {
    type: 'inputField',
    name: 'name',
    label: translate('common.labels.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'address',
    label: translate('common.labels.address'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'postalCode',
    label: translate('common.labels.postalCode'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'city',
    label: translate('common.labels.city'),
    rules: undefined
  },
  {
    type: 'phoneNumberField',
    name: 'phoneNumber',
    label: translate('common.labels.phoneNumber'),
    defaultDialCode: 'PT',
    rules: undefined,
    parseValue: (value: string) => {
      if (!value) return null;

      const match = value.match(/^\+(\d{1,4})(.+)$/);

      if (match) {
        return { dialCode: match[1], phone: match[2] };
      }

      return { dialCode: '351', phone: value };
    }
  },
  {
    type: 'selectField',
    name: 'countryCode',
    showSearch: true,
    allowClear: true,
    optionLabelProp: 'minimal',
    label: translate('common.labels.countryCode'),
    rules: undefined,
    options: {
      hook: useGetDialCodes as any,
      hookProps: () => {
        return [];
      },
      normalize: (items: DialCode[]) =>
        items?.map((item: DialCode) => ({
          value: item.code,
          searchValue: item.name,
          minimal: <DialCountry item={item} />,
          label: <DialCountry item={item} />
        }))
    }
  },
  {
    type: 'checkBoxField',
    name: 'homeDelivery',
    label: translate('common.labels.homeDelivery'),
    rules: undefined
  }
];

/**
 * Fiscal Info.
 */

const fiscalInfo = (translate: TFunction): FormList => [
  {
    type: 'render',
    name: 'leftWire1',
    render: <SubTitle>{translate('pharmacyForm.pharmacyFiscalTitle')}</SubTitle>
  },
  {
    type: 'inputField',
    name: 'fiscalName',
    label: translate('common.labels.fiscalName'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'fiscalAddress',
    label: translate('common.labels.fiscalAddress'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'fiscalPostalCode',
    label: translate('common.labels.fiscalPostalCode'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'fiscalCity',
    label: translate('common.labels.fiscalCity'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'nif',
    label: translate('common.labels.nif'),
    rules: undefined
  },
  {
    type: 'selectField',
    name: 'fiscalCountryCode',
    showSearch: true,
    allowClear: true,
    optionLabelProp: 'minimal',
    label: translate('common.labels.fiscalCountryCode'),
    rules: undefined,
    options: {
      hook: useGetDialCodes as any,
      hookProps: () => {
        return [];
      },
      normalize: (items: DialCode[]) =>
        items?.map((item: DialCode) => ({
          value: item.code,
          searchValue: item.name,
          minimal: <DialCountry item={item} />,
          label: <DialCountry item={item} />
        }))
    }
  }
];

/**
 * Contact.
 */

const contact = (translate: TFunction): FormList => [
  {
    type: 'render',
    name: 'leftWire1',
    render: <SubTitle>{translate('pharmacyForm.pharmacyContactTitle')}</SubTitle>
  },
  {
    type: 'selectField',
    name: 'contactUserId',
    showSearch: true,
    allowClear: true,
    optionLabelProp: 'minimal',
    onChange: (value, option, form) => {
      const data = (option as any)?.object;

      form.setValue('contactName', data.name);
      form.setValue('contactEmail', data.email);
      form.setValue('contactPhoneNumber', data.phoneNumber);
    },
    label: translate('common.labels.contactUser'),
    rules: undefined,
    paginated: { enabled: true, pageSize: 50 },
    options: {
      hook: usePaginatedCrudRequest as any,
      hookProps: (_values: any, _params: any, options: any) => {
        const searchValue = options?.searchValue?.trim();
        return [
          {
            key: ['users-paginated', searchValue || ''],
            endpoint: 'users' as any,
            options: {
              ...(searchValue ? { search: searchValue } : {})
            },
            pageSize: 50
          }
        ];
      },
      normalize: (items: User[]) => {
        if (!items || !Array.isArray(items)) {
          return [];
        }
        return items
          .filter(item => item && item.id)
          .map(item => ({
            value: item.id,
            label: item.name,
            searchValue: item.name,
            minimal: item.name,
            object: item
          }));
      }
    }
  },
  {
    type: 'inputField',
    disabled: values => !!values.contactUserId,
    name: 'contactName',
    label: translate('common.labels.contactName'),
    rules: undefined
  },
  {
    type: 'inputField',
    disabled: values => !!values.contactUserId,
    name: 'contactEmail',
    label: translate('common.labels.contactEmail'),
    rules: undefined
  },
  {
    type: 'phoneNumberField',
    name: 'contactPhoneNumber',
    label: translate('common.labels.contactPhoneNumber'),
    defaultDialCode: 'PT',
    disabled: values => !!values.contactUserId,
    rules: undefined,
    parseValue: (value: string) => {
      if (!value) return null;

      const match = value.match(/^\+(\d{1,4})(.+)$/);

      if (match) {
        return { dialCode: match[1], phone: match[2] };
      }

      return { dialCode: '351', phone: value };
    }
  }
];

/**
 * Right.
 */

const right = (translate: TFunction): FormList => [
  {
    type: 'render',
    name: 'leftWire1',
    render: <SubTitle>{translate('pharmacyForm.dataInfoTitle')}</SubTitle>
  },
  {
    type: 'inputField',
    name: 'id',
    disabled: true,
    label: translate('common.labels.id'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'createdAt',
    disabled: true,
    label: translate('common.labels.createdAt'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'updatedAt',
    disabled: true,
    label: translate('common.labels.updatedAt'),
    rules: undefined
  }
];

/**
 * Export `formFields`.
 */

export const formFields = (translate: TFunction): FormList => [
  {
    type: 'columns',
    gutter: 40,
    name: 'mainWire',
    columns: [
      {
        column: 6,
        children: left(translate)
      },
      {
        column: 6,
        children: contact(translate)
      },
      {
        column: 6,
        children: fiscalInfo(translate)
      },
      {
        column: 6,
        children: right(translate)
      }
    ]
  }
];
